import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Modal,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { ReloadOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppTranslation } from '../../../hooks/useAppTranslation';
import {
  useDismissLoyaltyPassVisitOutbox,
  useLoyaltyPassVisitOutbox,
  useRetryLoyaltyPassVisitOutbox,
} from '../../../hooks/useLoyaltyPassVisitOutbox';
import type {
  LoyaltyPassVisitOutboxDoc,
  LoyaltyPassVisitOutboxStatus,
} from '../../../utils/loyaltyPass';
import { formatNumber } from '../../../utils/numberFormat';

const { Text, Title } = Typography;

type FilterKey = 'pending' | 'all' | 'synced' | 'dismissed';

interface LoyaltyPassVisitOutboxTabContentProps {
  businessId: string;
  loyaltyPassEnabled?: boolean;
  /** Retry / dismiss réservés Super Admin (CSM : lecture seule). */
  canRetry?: boolean;
}

const STATUS_COLOR: Record<LoyaltyPassVisitOutboxStatus, string> = {
  pending: 'orange',
  retrying: 'processing',
  synced: 'success',
  failed_permanent: 'error',
  dismissed: 'default',
};

const LoyaltyPassVisitOutboxTabContent: React.FC<
  LoyaltyPassVisitOutboxTabContentProps
> = ({ businessId, loyaltyPassEnabled, canRetry = false }) => {
  const { t } = useAppTranslation(['common']);
  const { data = [], isLoading, refetch, isFetching } = useLoyaltyPassVisitOutbox(
    businessId
  );
  const retryMutation = useRetryLoyaltyPassVisitOutbox(businessId);
  const dismissMutation = useDismissLoyaltyPassVisitOutbox(businessId);
  const [filter, setFilter] = useState<FilterKey>('pending');
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const statusLabel = (status: LoyaltyPassVisitOutboxStatus) => {
    switch (status) {
      case 'pending':
        return t('loyaltyPassVisitOutbox.statusPending');
      case 'retrying':
        return t('loyaltyPassVisitOutbox.statusRetrying');
      case 'synced':
        return t('loyaltyPassVisitOutbox.statusSynced');
      case 'failed_permanent':
        return t('loyaltyPassVisitOutbox.statusFailedPermanent');
      case 'dismissed':
        return t('loyaltyPassVisitOutbox.statusDismissed');
      default:
        return status;
    }
  };

  const rows = useMemo(() => {
    if (filter === 'all') return data;
    if (filter === 'pending') {
      return data.filter(
        (d) => d.status === 'pending' || d.status === 'retrying' || d.status === 'failed_permanent'
      );
    }
    if (filter === 'synced') return data.filter((d) => d.status === 'synced');
    return data.filter((d) => d.status === 'dismissed');
  }, [data, filter]);

  const handleRetry = async (row: LoyaltyPassVisitOutboxDoc) => {
    setRetryingId(row.transactionGroupId);
    try {
      const res = await retryMutation.mutateAsync(row.transactionGroupId);
      if (res.synced) {
        message.success(
          res.alreadyDone
            ? t('loyaltyPassVisitOutbox.retryAlreadyDone')
            : t('loyaltyPassVisitOutbox.retrySuccess')
        );
      } else {
        message.error(
          t('loyaltyPassVisitOutbox.retryFailed', {
            reason: res.reason || 'UNKNOWN',
          })
        );
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message)
          : 'UNKNOWN';
      message.error(t('loyaltyPassVisitOutbox.retryFailed', { reason: msg }));
    } finally {
      setRetryingId(null);
    }
  };

  const handleDismiss = (row: LoyaltyPassVisitOutboxDoc) => {
    Modal.confirm({
      title: t('loyaltyPassVisitOutbox.dismissConfirmTitle'),
      content: t('loyaltyPassVisitOutbox.dismissConfirmBody'),
      okText: t('loyaltyPassVisitOutbox.dismiss'),
      cancelText: t('actions.cancel'),
      onOk: async () => {
        await dismissMutation.mutateAsync(row.transactionGroupId);
        message.success(t('loyaltyPassVisitOutbox.dismissSuccess'));
      },
    });
  };

  const columns: ColumnsType<LoyaltyPassVisitOutboxDoc> = [
    {
      title: t('loyaltyPassVisitOutbox.colDate'),
      key: 'createdAt',
      width: 160,
      render: (_, row) =>
        row.createdAt
          ? row.createdAt.toLocaleString()
          : row.updatedAt?.toLocaleString() || '—',
    },
    {
      title: t('loyaltyPassVisitOutbox.colClient'),
      key: 'client',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text>{row.clientName || t('loyaltyPassVisitOutbox.unknownClient')}</Text>
          {row.clientPhone ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.clientPhone}
            </Text>
          ) : null}
          {row.passId ? (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {row.passId}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: t('loyaltyPassVisitOutbox.colSale'),
      dataIndex: 'transactionGroupId',
      key: 'transactionGroupId',
      ellipsis: true,
      width: 140,
    },
    {
      title: t('loyaltyPassVisitOutbox.colAmount'),
      key: 'amount',
      width: 110,
      render: (_, row) => `${formatNumber(row.ticketAmountXaf, 0)} FCFA`,
    },
    {
      title: t('loyaltyPassVisitOutbox.colReason'),
      dataIndex: 'lastErrorReason',
      key: 'lastErrorReason',
      width: 140,
      render: (v?: string) => v || '—',
    },
    {
      title: t('loyaltyPassVisitOutbox.colAttempts'),
      dataIndex: 'attemptCount',
      key: 'attemptCount',
      width: 90,
    },
    {
      title: t('loyaltyPassVisitOutbox.colStatus'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: LoyaltyPassVisitOutboxStatus) => (
        <Tag color={STATUS_COLOR[status] || 'default'}>{statusLabel(status)}</Tag>
      ),
    },
    {
      title: t('loyaltyPassVisitOutbox.colActions'),
      key: 'actions',
      width: 200,
      render: (_, row) => {
        const canAct =
          canRetry &&
          (row.status === 'pending' ||
            row.status === 'retrying' ||
            row.status === 'failed_permanent');
        if (!canAct) return null;
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<ReloadOutlined />}
              loading={retryingId === row.transactionGroupId}
              onClick={() => handleRetry(row)}
            >
              {t('loyaltyPassVisitOutbox.retry')}
            </Button>
            <Button
              size="small"
              icon={<StopOutlined />}
              onClick={() => handleDismiss(row)}
              loading={dismissMutation.isPending}
            >
              {t('loyaltyPassVisitOutbox.dismiss')}
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="business-detail-tab">
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {t('loyaltyPassVisitOutbox.title')}
            </Title>
            <Text type="secondary">{t('loyaltyPassVisitOutbox.subtitle')}</Text>
          </div>
          <Button icon={<SyncOutlined />} onClick={() => refetch()} loading={isFetching}>
            {t('actions.retry')}
          </Button>
        </div>

        {!loyaltyPassEnabled ? (
          <Alert type="info" showIcon message={t('loyaltyPassVisitOutbox.featureOffHint')} />
        ) : null}

        <Segmented
          value={filter}
          onChange={(v) => setFilter(v as FilterKey)}
          options={[
            { label: t('loyaltyPassVisitOutbox.filterPending'), value: 'pending' },
            { label: t('loyaltyPassVisitOutbox.filterAll'), value: 'all' },
            { label: t('loyaltyPassVisitOutbox.filterSynced'), value: 'synced' },
            { label: t('loyaltyPassVisitOutbox.filterDismissed'), value: 'dismissed' },
          ]}
        />

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <Empty description={t('loyaltyPassVisitOutbox.empty')} />
        ) : (
          <Table
            rowKey="id"
            size="middle"
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 1100 }}
          />
        )}
      </Space>
    </div>
  );
};

export default LoyaltyPassVisitOutboxTabContent;
